import { Check, Clock, Copy, Mail, Plus, Trash2, UserCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentUser } from "../context/CurrentUserContext";
import { apiClient } from "../lib/apiClient";

interface TeamMember {
	id: string;
	userId: string;
	email: string | null;
	role: string;
	joinedAt: string;
}

interface PendingInvite {
	id: string;
	email: string;
	token: string;
	expiresAt: string;
	createdAt: string;
}

export default function Team() {
	const { currentUser, isLoading: isUserLoading } = useCurrentUser();
	const isOwner = currentUser?.role === "owner";

	const [members, setMembers] = useState<TeamMember[]>([]);
	const [invites, setInvites] = useState<PendingInvite[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const [revokingId, setRevokingId] = useState<string | null>(null);

	useEffect(() => {
		if (isUserLoading) {
			return;
		}

		let cancelled = false;
		setError(null);
		setIsLoading(true);

		const fetchMembers = apiClient().get<TeamMember[]>("/team/members");
		const fetchInvites = isOwner
			? apiClient().get<PendingInvite[]>("/team/invites")
			: Promise.resolve(null);

		Promise.all([fetchMembers, fetchInvites])
			.then(([membersRes, invitesRes]) => {
				if (cancelled) {
					return;
				}
				setMembers(membersRes.data);
				if (invitesRes) setInvites(invitesRes.data);
			})
			.catch(() => {
				if (!cancelled) {
					setError("Failed to load team data.");
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [isOwner, isUserLoading]);

	const handleCopy = async (invite: PendingInvite) => {
		const url = `${window.location.origin}/accept-invite?token=${invite.token}`;
		await navigator.clipboard.writeText(url);
		setCopiedId(invite.id);
		setTimeout(() => setCopiedId(null), 2000);
	};

	const handleRevoke = async (inviteId: string) => {
		setRevokingId(inviteId);
		try {
			await apiClient().delete(`/team/invites/${inviteId}`);
			setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
		} catch {
			// silently leave the invite in list on error
		} finally {
			setRevokingId(null);
		}
	};

	const getRoleLabel = (role: string) => (role === "owner" ? "Owner" : "Member");

	const getRoleBadgeClass = (role: string) =>
		role === "owner" ? "bg-orange-100 text-orange-800" : "bg-blue-100 text-blue-800";

	const getInitials = (email: string | null) => (email ? email[0].toUpperCase() : "?");

	return (
		<div className="min-h-screen w-full bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<main className="p-4 sm:p-8">
				<div className="max-w-6xl mx-auto">
					{/* Header */}
					<div className="flex flex-col md:flex-row justify-between items-center mb-8">
						<h1 className="text-2xl font-semibold text-gray-900">Team Members</h1>
						<Link
							to="/team/add"
							className="group bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold px-4 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2 mt-3 w-auto md:mt-0"
						>
							<Plus className="w-5 h-5" />
							Invite a Team Member
						</Link>
					</div>

					{/* Members card */}
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
						<div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
							<h2 className="text-xl font-semibold text-gray-900 mb-2 sm:mb-0">Your Team</h2>
							{!isLoading && (
								<span className="text-sm text-gray-500">
									{members.length} member{members.length !== 1 ? "s" : ""}
								</span>
							)}
						</div>

						{isLoading && (
							<div className="text-center py-8 text-gray-500">Loading team members…</div>
						)}

						{error && <div className="text-center py-8 text-red-500">{error}</div>}

						{!isLoading && !error && members.length === 0 && (
							<div className="text-center py-8">
								<UserCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
								<h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
								<p className="text-gray-500 mb-4">Start building your team by inviting members.</p>
								<Link
									to="/team/add"
									className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold px-4 py-2 rounded-full transition-all duration-300 shadow-lg"
								>
									<Plus className="w-5 h-5" />
									Invite a Team Member
								</Link>
							</div>
						)}

						{!isLoading && !error && members.length > 0 && (
							<div className="space-y-3">
								{members.map((member) => (
									<div
										key={member.id}
										className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
									>
										<div className="flex items-center gap-3 min-w-0 flex-1">
											<div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-lg flex-shrink-0">
												{getInitials(member.email)}
											</div>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-1 sm:gap-2 text-sm text-gray-500">
													<Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
													<span className="truncate">{member.email ?? "—"}</span>
												</div>
											</div>
										</div>
										<div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
											<span
												className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(member.role)}`}
											>
												{getRoleLabel(member.role)}
											</span>
											<p className="text-xs text-gray-500">
												Joined {new Date(member.joinedAt).toLocaleDateString("en-GB")}
											</p>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Pending Invites card — owners only */}
					{isOwner && !isLoading && !error && (
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
							<div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
								<h2 className="text-xl font-semibold text-gray-900 mb-2 sm:mb-0">
									Pending Invites
								</h2>
								<span className="text-sm text-gray-500">{invites.length} pending</span>
							</div>

							{invites.length === 0 ? (
								<div className="text-center py-6">
									<Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
									<p className="text-gray-500 text-sm">No pending invitations.</p>
								</div>
							) : (
								<div className="space-y-3">
									{invites.map((invite) => (
										<div
											key={invite.id}
											className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
										>
											<div className="flex items-center gap-3 min-w-0 flex-1">
												<div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
													<Mail className="w-5 h-5 text-gray-400" />
												</div>
												<div className="min-w-0 flex-1">
													<p className="text-sm text-gray-800 truncate font-medium">
														{invite.email}
													</p>
													<p className="text-xs text-gray-400 mt-0.5">
														Expires {new Date(invite.expiresAt).toLocaleDateString("en-GB")}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2 flex-shrink-0 ml-3">
												<button
													type="button"
													onClick={() => handleCopy(invite)}
													title="Copy invite link"
													className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:border-orange-300 hover:text-orange-600 transition-colors"
												>
													{copiedId === invite.id ? (
														<>
															<Check className="w-3.5 h-3.5 text-green-500" />
															<span className="text-green-600">Copied</span>
														</>
													) : (
														<>
															<Copy className="w-3.5 h-3.5" />
															<span>Copy link</span>
														</>
													)}
												</button>
												<button
													type="button"
													onClick={() => handleRevoke(invite.id)}
													disabled={revokingId === invite.id}
													title="Revoke invite"
													className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
												>
													{revokingId === invite.id ? (
														<div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
													) : (
														<X className="w-4 h-4" />
													)}
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{/* Stats */}
					{!isLoading && !error && (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
							<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
										<UserCircle className="w-6 h-6 text-orange-600" />
									</div>
									<div>
										<p className="text-sm font-medium text-gray-600">Members</p>
										<p className="text-2xl font-bold text-gray-900">
											{members.filter((m) => m.role === "member").length}
										</p>
									</div>
								</div>
							</div>
							<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
										<UserCircle className="w-6 h-6 text-blue-600" />
									</div>
									<div>
										<p className="text-sm font-medium text-gray-600">Total</p>
										<p className="text-2xl font-bold text-gray-900">{members.length}</p>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
